import { Gift, Sparkles, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

interface LoyaltyState {
  enabled: boolean;
  balance: number;
  program?: {
    redemptionValueMinorPerPoint: number;
    minRedemptionPoints: number;
  };
}

interface ReferralState {
  enabled: boolean;
  code?: { code: string };
  program?: {
    refereeRewardMinor: number;
    referrerRewardMinor: number;
    currency: string;
  };
}

interface PackageOffering {
  id: string;
  name: string;
  quantity: number;
  priceMinor: number;
  currency: string;
  validityDays: number;
}

interface CustomerPackage {
  id: string;
  remainingQuantity: number;
  originalQuantity: number;
  expiresAt: string;
  status: string;
}

interface GiftCardEntry {
  id: string;
  code: string;
  remainingValueMinor: number;
  currency: string;
  status: string;
}

function fmtMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

/**
 * Combined customer-facing loyalty/referrals/gift-cards/packages dashboard.
 * Mounted at /account/loyalty.
 */
export function LoyaltyRewardsPage() {
  const [loyalty, setLoyalty] = useState<LoyaltyState | null>(null);
  const [referral, setReferral] = useState<ReferralState | null>(null);
  const [offerings, setOfferings] = useState<PackageOffering[]>([]);
  const [myPacks, setMyPacks] = useState<CustomerPackage[]>([]);
  const [myGiftCards, setMyGiftCards] = useState<GiftCardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // Per-endpoint ok checks: a failed response must not be parsed into
        // state as if it were data (error bodies would render as garbage).
        const jsonOrNull = (r: Response) => (r.ok ? r.json() : null);
        const [lb, rc, po, pm, gc] = await Promise.all([
          fetch('/api/customer/loyalty/balance', { credentials: 'include' }).then(jsonOrNull),
          fetch('/api/customer/referrals/code', { credentials: 'include' }).then(jsonOrNull),
          fetch('/api/customer/packages/offerings', { credentials: 'include' }).then(jsonOrNull),
          fetch('/api/customer/packages/mine', { credentials: 'include' }).then(jsonOrNull),
          fetch('/api/customer/gift-cards/mine', { credentials: 'include' }).then(jsonOrNull),
        ]);
        setLoyalty(lb);
        setReferral(rc);
        setOfferings(po?.offerings ?? []);
        setMyPacks(pm?.rows ?? []);
        setMyGiftCards([...(gc?.purchased ?? []), ...(gc?.redeemable ?? [])]);
      } catch {
        // best-effort — page renders with defaults if APIs fail
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function copyCode(code: string) {
    await navigator.clipboard.writeText(code);
    setCopyMsg(`Copied "${code}"`);
    setTimeout(() => setCopyMsg(null), 2000);
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 px-4 py-6 sm:px-6 sm:py-10">
      <h1 className="text-3xl font-semibold">Rewards</h1>
      {copyMsg && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {copyMsg}
        </div>
      )}

      {/* Loyalty */}
      {loyalty?.enabled && (
        <section className="rounded-lg border bg-card p-6">
          <header className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand-blue" />
            <h2 className="text-xl font-semibold">Loyalty points</h2>
          </header>
          <p className="mt-3 text-3xl font-semibold">
            <span className="num">{loyalty.balance.toLocaleString()}</span> pts
          </p>
          {loyalty.program && (
            <p className="mt-1 text-sm text-muted-foreground">
              {loyalty.program.minRedemptionPoints}+ pts to redeem · 1 pt ≈{' '}
              {fmtMoney(loyalty.program.redemptionValueMinorPerPoint, 'GBP')}
            </p>
          )}
        </section>
      )}

      {/* Referrals */}
      {referral?.enabled && referral.code && referral.program && (
        <section className="rounded-lg border bg-card p-6">
          <header className="flex items-center gap-2">
            <Users className="h-5 w-5 text-brand-blue" />
            <h2 className="text-xl font-semibold">Refer a friend</h2>
          </header>
          <p className="mt-2 text-sm text-muted-foreground">
            Share your code. Your friend gets{' '}
            {fmtMoney(referral.program.refereeRewardMinor, referral.program.currency)}, and you get{' '}
            {fmtMoney(referral.program.referrerRewardMinor, referral.program.currency)} when they
            book their first stay.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <code className="rounded-md bg-muted px-3 py-2 text-lg font-mono">
              {referral.code.code}
            </code>
            <button
              type="button"
              onClick={() => referral.code && copyCode(referral.code.code)}
              className="rounded-md bg-brand-blue px-3 py-2 text-sm font-medium text-white"
            >
              Copy code
            </button>
          </div>
        </section>
      )}

      {/* Gift cards */}
      {myGiftCards.length > 0 && (
        <section className="rounded-lg border bg-card p-6">
          <header className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-brand-blue" />
            <h2 className="text-xl font-semibold">Your gift cards</h2>
          </header>
          <ul className="mt-3 space-y-2 text-sm">
            {myGiftCards.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <code className="font-mono">{c.code}</code>
                <span>{fmtMoney(c.remainingValueMinor, c.currency)} remaining</span>
                <span className="text-xs text-muted-foreground">{c.status}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* My packages */}
      {myPacks.length > 0 && (
        <section className="rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold">Your stay packages</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {myPacks.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <span>
                  {p.remainingQuantity} / {p.originalQuantity} stays remaining
                </span>
                <span className="text-xs text-muted-foreground">
                  expires {new Date(p.expiresAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Package offerings (purchase) */}
      {offerings.length > 0 && (
        <section className="rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold">Buy a stay package</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pre-pay for multiple stays at a discount.
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            {offerings.map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <span>
                  <strong>{o.name}</strong> — {o.quantity} stays
                </span>
                <span>{fmtMoney(o.priceMinor, o.currency)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
